import { Props } from "./types";
import { SidebarItem } from "../Sidebar-Item/SidebarItem";
import { SidebarProps } from "../Sidebar-Item/types";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import DataContainer from "../../services/DataContainer";
import { MainCategories, MainSettings } from "../../enum/enums";
import { DataContainerContext } from "../../context/DataContainerContext";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AdobeCampaignService, _allPreviousChoice } from "../../services/adobe/AdobeCampaign";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { handleKeyboardNavigation } from "../../utils/accessibility";

export const List = ({
  items,
  paddingX,
  paddingY,
  className,
  onClickItemEvent,
  singleItemClassNames,
  hideEmptyImage,
  setToggleSidebar,
  toggleSidebar,
  pageName
}: Props) => {
  const location = useLocation().pathname;
  const navigate = useNavigate();
  const { currentLocale, activeComponentGroups, modelName} = useContext(DataContainerContext);
  const [currentProduct, setCurrentProduct] = useState<any>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const listRef = useRef<HTMLUListElement>(null);
  const adobeService = new AdobeCampaignService();
  const prevActiveComponentGroupsName = adobeService.usePrevious(activeComponentGroups.name);
  

  useEffect(() => {
    setCurrentProduct(DataContainer.mLoadedData);
  },[DataContainer.mLoadedData]);
  
  // Create callbacks for special items
  const handleSegmentClick = useCallback(() => {
    navigate("/"+currentLocale?.locale);
  }, [navigate, currentLocale]);
  
  const handleConfiguratorSummaryClick = useCallback(() => {
    adobeService.sendAnalytics(AdobeEventType.PAGE_ENTERED, {category: prevActiveComponentGroupsName}, _allPreviousChoice);
    navigate(`/${currentLocale?.locale}/summary/${modelName}/${DataContainer.generateUniqueHashForSelections()}`);
  }, [adobeService, prevActiveComponentGroupsName, navigate, currentLocale, modelName]);
  
  const handleSummaryPageClick = useCallback(() => {
    setToggleSidebar?.(false);
  }, [setToggleSidebar]);

  const sidebarItemBottomBorder = (index: number) => {
    if ((pageName === "configurator" && index === items.length) || (pageName !== "configurator" && index === items.length -1)) {
      return false;
    }
    return  true;
  };

  // Calculate total number of items including special items
  const getTotalItems = () => {
    let total = items?.length || 0;
    if (location.includes("/configurator") || location.includes("/summary")) {
      total += 1; // Add segment item
    }
    if (location.includes("/configurator")) {
      total += 1; // Add summary item
    }
    if (location.includes("/summary")) {
      total += 1; // Add summary item
    }
    return total;
  };

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const totalItems = getTotalItems();
    
    handleKeyboardNavigation(
      e,
      () => {
        // Handle Enter/Space activation
        // Handle segment item (index 0) when it exists
        if (focusedIndex === 0 && (location.includes("/configurator") || location.includes("/summary"))) {
          handleSegmentClick();
          return;
        }
        
        // Handle summary item (last item)
        const hasSegment = location.includes("/configurator") || location.includes("/summary");
        const summaryIndex = hasSegment ? (items?.length || 0) + 1 : (items?.length || 0);
        if (focusedIndex === summaryIndex) {
          if (location.includes("/configurator")) {
            handleConfiguratorSummaryClick();
          } else if (location.includes("/summary")) {
            handleSummaryPageClick();
          }
          return;
        }
        
        // Handle regular items
        if (focusedIndex >= 0 && focusedIndex < totalItems) {
          if (hasSegment) {
            // Adjusted index for regular items when segment exists
            const itemIndex = focusedIndex - 1;
            if (itemIndex >= 0 && itemIndex < (items?.length || 0)) {
              const item = items[itemIndex];
              onClickItemEvent({name: item.name ? item.name : item.title, id: item.id, index: itemIndex, segment: item.link});
            }
          } else {
            // No segment, regular items start at index 0
            const item = items[focusedIndex];
            if (item) {
              onClickItemEvent({name: item.name ? item.name : item.title, id: item.id, index: focusedIndex, segment: item.link});
            }
          }
        }
      },
      () => {
        // Handle next (ArrowDown)
        setFocusedIndex(prev => {
          const nextIndex = prev < totalItems - 1 ? prev + 1 : 0;
          return nextIndex;
        });
      },
      () => {
        // Handle previous (ArrowUp)
        setFocusedIndex(prev => {
          const prevIndex = prev > 0 ? prev - 1 : totalItems - 1;
          return prevIndex;
        });
      }
    );
    
    // Handle Home and End keys
    if (e.key === "Home") {
      e.preventDefault();
      setFocusedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setFocusedIndex(totalItems - 1);
    }
  };
  
  return (
    <ul
      ref={listRef}
      className={classNames(
        "bg-dark-grey",
        { [`px-${paddingX}`]: paddingX, [`py-${paddingY}`]: paddingY },
        className
      )}
      role="list"
      aria-label="Navigation menu - Use arrow keys to navigate, Enter or Space to select"
      tabIndex={0}
      onKeyDown={handleListKeyDown}
    >
      {(location.includes("/configurator") || location.includes("/summary")) && 
      <SidebarItem
        onClickEvent={() => {
          navigate("/"+currentLocale?.locale);
        }}
        border={true}
        title={DataContainer.sectionKey("TruckSelector", "truck_selector_title")}
        number="01"
        productName={currentProduct && currentProduct?.product_info?.name}
        singleItemClassNames={singleItemClassNames}
        link={""}
        hideEmptyImage={hideEmptyImage}
        pageName={pageName}
      />}
      
      {items?.map((item: SidebarProps, index: number) => {
        const adjustedIndex = (location.includes("/configurator") || location.includes("/summary")) ? index + 1 : index;
        return (
          <li 
            key={item.id} 
            role="listitem"
          >
            <SidebarItem
              onClickEvent={() => {
                onClickItemEvent({name: item.name ? item.name : item.title, id: item.id, index: index, segment: item.link});
                (!location.includes("/configurator") && !location.includes("/summary"))
                && localStorage.setItem("segment", item.link);
              }}
              key={item.id}
              title={item.name ? DataContainer.sectionKey(MainSettings.MAIN_CATEGORIES, item.name.toLowerCase()) : item.title}
              number={`${index < 10 ? `0${(location.includes("/configurator") || location.includes("/summary")) ? index+2 : index+1}` : index+1}`}
              productName={item.productName}
              border={sidebarItemBottomBorder(index)}
              icon={item.icon}
              imageUrl={item.imageUrl}
              link={item.link}
              hideEmptyImage={hideEmptyImage}
              singleItemClassNames={singleItemClassNames}
              pageName={pageName}
              isFocused={focusedIndex === adjustedIndex}
              onFocus={() => setFocusedIndex(adjustedIndex)}
              toggleSidebar={toggleSidebar}
            />
          </li>
        );
      })}

      {location.includes("/configurator") &&
      <SidebarItem
        onClickEvent={() => {
          adobeService.sendAnalytics(AdobeEventType.PAGE_ENTERED, {category: prevActiveComponentGroupsName}, _allPreviousChoice);
          navigate(`/${currentLocale?.locale}/summary/${modelName}/${DataContainer.generateUniqueHashForSelections()}`);
        }}
        title={DataContainer.sectionKey(MainSettings.MAIN_CATEGORIES,MainCategories.SUMMARY_CATEGORY)}
        number={"0" + (items?.length + 2)}
        link={""}
        singleItemClassNames={singleItemClassNames}
        hideEmptyImage={hideEmptyImage}
        pageName={pageName}
      />}
      
      {location.includes("/summary") &&
        <li role="listitem">
          <SidebarItem
            onClickEvent={handleSummaryPageClick}
            title={DataContainer.sectionKey(MainSettings.MAIN_CATEGORIES,MainCategories.SUMMARY_CATEGORY)}
            number={"0" + (items?.length + 2)}
            link={""}
            singleItemClassNames={singleItemClassNames}
            hideEmptyImage={hideEmptyImage}
            pageName={pageName}
            isFocused={focusedIndex === (items?.length || 0) + 1}
            onFocus={() => setFocusedIndex((items?.length || 0) + 1)}
            toggleSidebar={toggleSidebar}
          />
        </li>
      }
    </ul>
  );
};
